/* eslint-disable react/prop-types */
/**
 * PrepareClaimDialog — the biller "Prepare Claim - Review Note & Client
 * Information" dialog, extracted VERBATIM from App.js (Jun 2026,
 * Prepare-Claim V2 migration Phase 1 — strangler pattern, same as
 * NoteDetailDialog).
 *
 * All state + handlers stay in the host and arrive via the `ctx` prop:
 *   • Legacy host: App.js supplies its existing state graph — zero
 *     behavior change on /dashboard.
 *   • V2 host (Phase 2): a native /v2 host will supply the same
 *     contract without the legacy Dashboard.
 * Do NOT add local state here without checking both hosts.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import FormFieldRenderer from '../forms/FormFieldRenderer';
import {
  AlertCircle, Archive, Ban, Calendar as CalendarIcon, CheckCircle, ChevronLeft,
  ChevronRight, CreditCard, ExternalLink, Eye, FileText, Link2, MapPin, Pencil, Plus,
  Receipt, RefreshCw, Shield, Stethoscope, Video, XCircle,
} from 'lucide-react';

export const PrepareClaimDialog = ({ ctx }) => {
  const {
    allClientsForLookup,
    appointments,
    availablePANs,
    billingPreviewClaimId,
    clientDiagnoses,
    clientGroups,
    clientMedications,
    clientVitals,
    cptCodes,
    currentNoteIndex,
    dryRunPreviewData,
    editingServiceLine,
    fetchClaimsQueue,
    fetchClientBilling,
    fetchNoteForReview,
    fetchTreatmentPlanGoals,
    filteredBillingNotes,
    findClientById,
    formTemplates,
    formatDateStringForDisplay,
    formatStatusLabel,
    formatTimeTo12Hour,
    getClientAllInsurances,
    getEffectivePosCodes,
    getSignatureUrl,
    handleClientClick,
    insurances,
    linkedGoals,
    linkingClaimId,
    loadedSignatures,
    loadingDryRun,
    locations,
    navigate,
    navigateToNextBillingNoteNoRemove,
    navigateToPrevBillingNote,
    newNoteFormData,
    noteReviewData,
    openAppointment,
    openNote,
    organizations,
    parseUTCTimestamp,
    posCodes,
    providers,
    savingServiceLine,
    selectedClient,
    selectedNote,
    selectedNoteClients,
    serviceLineEdits,
    serviceTypes,
    sortedFilteredBillingNotes,
    telehealthConsent,
    user,
    users,
    setAppointments,
    setBillingPreviewClaimId,
    setClientDetailTab,
    setCurrentNoteIndex,
    setDryRunPreviewData,
    setEditingMedication,
    setEditingServiceLine,
    setLinkedGoals,
    setLinkingClaimId,
    setLoadedSignatures,
    setLoadingDryRun,
    setManagingInsuranceForClient,
    setNewNoteFormData,
    setNotBillableComment,
    setNotBillableReason,
    setNoteReviewData,
    setNotes,
    setNotesReadyForBilling,
    setSavingServiceLine,
    setSelectedClient,
    setSelectedNoteForReview,
    setServiceLineEdits,
    setShowClientDetail,
    setShowDiagnosisForm,
    setShowInsuranceManagement,
    setShowLinkExistingClaimDialog,
    setShowLinkGoalsDialog,
    setShowMedicationForm,
    setShowNotBillableDialog,
    setShowNoteReviewDialog,
    setShowSendBackDialog,
    setShowSingleLegacyMarkDialog,
    setTelehealthConsent,
    showNoteReviewDialog,
    API,
  } = ctx;

  return (
          <Dialog open={showNoteReviewDialog} onOpenChange={(o) => { setShowNoteReviewDialog(o); if (!o) { setBillingPreviewClaimId(null); setDryRunPreviewData(null); setEditingServiceLine(false); } }}>
            <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle>Prepare Claim - Review Note & Client Information</DialogTitle>
                    <DialogDescription>
                      Verify note content, signatures, and appointment details before billing the claim
                      {filteredBillingNotes.length > 1 && (
                        <span className="ml-2">
                          (Note {currentNoteIndex + 1} of {sortedFilteredBillingNotes.length})
                        </span>
                      )}
                    </DialogDescription>
                  </div>
                  {filteredBillingNotes.length > 1 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={navigateToPrevBillingNote}
                        disabled={filteredBillingNotes.length <= 1}
                        data-testid="prepare-claim-prev"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={navigateToNextBillingNoteNoRemove}
                        disabled={filteredBillingNotes.length <= 1}
                        data-testid="prepare-claim-next"
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </DialogHeader>
              
              {noteReviewData && (
                <div className="space-y-6">
                  {/* Duplicate Warning */}
                  {/* Duplicate Warnings — Enhanced with content, template, and CPT checks */}
                  {noteReviewData.duplicateWarnings && noteReviewData.duplicateWarnings.length > 0 && (
                    <Alert className="border-orange-600 bg-orange-50" data-testid="duplicate-warning-alert">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-900">
                        <strong>Duplicate Warning:</strong> Found {noteReviewData.duplicateWarnings.length} other note(s) for this client on the same date.
                        <ul className="mt-2 space-y-1.5 text-sm">
                          {noteReviewData.duplicateWarnings.map((dw, i) => {
                            const dupDos = dw.note.date_of_service;
                            const dupDosFormatted = dupDos ? new Date(dupDos + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                            return (
                              <li key={i} className="flex flex-col gap-0.5 border-l-2 border-orange-300 pl-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    className="font-medium text-orange-900 hover:text-orange-700 underline decoration-orange-400 underline-offset-2 inline-flex items-center gap-1"
                                    data-testid={`duplicate-open-note-${dw.note.id}`}
                                    onClick={() => {
                                      // Jun 2026 — keep the Prepare Claim
                                      // dialog open; the note opens on top
                                      // (or restores on return in V2).
                                      openNote(dw.note, true);
                                    }}
                                    title="Open this note in the full editor"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    {dw.note.template_name || 'Note'}
                                  </button>
                                  {dupDosFormatted && (
                                    <span className="text-xs text-orange-700">
                                      DOS: <span className="font-medium">{dupDosFormatted}</span>
                                    </span>
                                  )}
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-orange-200 text-orange-900">
                                    {(dw.note.status || '').replace(/_/g, ' ')}
                                  </span>
                                  {(dw.note.cpt_code || dw.note._billing?.cpt_code) && (
                                    <span className="text-xs text-orange-700">
                                      CPT: <span className="font-mono font-medium">{dw.note.cpt_code || dw.note._billing?.cpt_code}</span>
                                    </span>
                                  )}
                                </div>
                                {dw.warnings.map((w, wi) => (
                                  <span key={wi} className="text-orange-700 text-xs flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-orange-500 flex-shrink-0" />
                                    {w}
                                  </span>
                                ))}
                              </li>
                            );
                          })}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}
                  {noteReviewData.potentialDuplicates && noteReviewData.potentialDuplicates.length > 0 && (!noteReviewData.duplicateWarnings || noteReviewData.duplicateWarnings.length === 0) && (
                    <Alert className="border-orange-600 bg-orange-50">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-900">
                        <strong>Warning:</strong> Found {noteReviewData.potentialDuplicates.length} other note(s) for this client on the same date. Please verify this is not a duplicate.
                      </AlertDescription>
                    </Alert>
                  )}
                  {noteReviewData.existingClaimWarning && (
                    <Alert className="border-red-500 bg-red-50" data-testid="existing-claim-banner">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-900">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <strong>Existing Claim:</strong> {noteReviewData.existingClaimWarning}
                            {(noteReviewData.existingClaims || []).length > 0 && (
                              <div className="mt-2 space-y-1.5">
                                {noteReviewData.existingClaims.map((c) => {
                                  const isThisNote = c.note_id === noteReviewData.note?.id;
                                  return (
                                    <div
                                      key={c.id}
                                      className="flex items-center gap-2 flex-wrap"
                                      data-testid={`existing-claim-row-${c.claim_number || c.id}`}
                                    >
                                      <span className="font-mono text-xs px-2 py-0.5 bg-white border border-red-300 rounded">
                                        {c.claim_number || c.id.slice(0, 8)}
                                      </span>
                                      <span className="text-xs text-red-800">
                                        status: {c.status} · ${(c.billed_amount || 0).toFixed?.(2) ?? c.billed_amount}
                                      </span>
                                      {isThisNote && (
                                        <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded">
                                          matches this note
                                        </span>
                                      )}
                                      {!noteReviewData.note?.claim_id && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2 text-xs border-blue-600 text-blue-700 hover:bg-blue-50"
                                          data-testid={`existing-claim-link-btn-${c.claim_number || c.id}`}
                                          disabled={linkingClaimId !== null}
                                          onClick={async () => {
                                            try {
                                              setLinkingClaimId(c.id);
                                              const body = isThisNote ? { claim_id: c.id } : { claim_id: c.id, force_link: true };
                                              await axios.post(
                                                `${API}/billing/notes/${noteReviewData.note.id}/link-existing-claim`,
                                                body
                                              );
                                              toast.success(`Linked to ${c.claim_number || c.id.slice(0, 8)}`);
                                              const processedNoteId = noteReviewData.note.id;
                                              setNotesReadyForBilling(prev => prev.filter(n => n.id !== processedNoteId));
                                              setShowNoteReviewDialog(false);
                                            } catch (e) {
                                              toast.error(`Link failed: ${e.response?.data?.detail || e.message}`);
                                            } finally {
                                              setLinkingClaimId(null);
                                            }
                                          }}
                                        >
                                          <Link2 className="h-3 w-3 mr-1" />
                                          {linkingClaimId === c.id ? 'Linking…' : 'Link'}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Correction banner — red when actively needing corrections, green (resolved) when the provider has fixed + re-signed */}
                  {(noteReviewData.note.needs_corrections_reason || noteReviewData.note.biller_notes || noteReviewData.note.status === 'needs_corrections') && (
                    noteReviewData.note.status === 'needs_corrections' ? (
                      <Alert className="border-red-500 bg-red-50" data-testid="note-corrections-banner-open">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-900">
                          <strong>Needs Correction:</strong> {noteReviewData.note.needs_corrections_reason || noteReviewData.note.biller_notes || 'Returned for corrections'}
                          {noteReviewData.note.returned_by_biller && (
                            <span className="text-red-700 text-xs ml-2">— {noteReviewData.note.returned_by_biller}</span>
                          )}
                          {noteReviewData.note.corrections_requested_at && (
                            <span className="text-red-700 text-xs ml-2">
                              · Requested {new Date(noteReviewData.note.corrections_requested_at).toLocaleString()}
                            </span>
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert className="border-green-500 bg-green-50" data-testid="note-corrections-banner-resolved">
                        <CheckCircle className="h-4 w-4 text-green-700" />
                        <AlertDescription className="text-green-900">
                          <strong>Corrections Resolved.</strong>
                          {' '}
                          <span className="text-green-800">Original issue: "{noteReviewData.note.needs_corrections_reason || noteReviewData.note.biller_notes}"</span>
                          {noteReviewData.note.returned_by_biller && (
                            <span className="text-green-700 text-xs ml-2">— returned by {noteReviewData.note.returned_by_biller}</span>
                          )}
                          {noteReviewData.note.corrections_resolved_at && (
                            <div className="text-green-700 text-xs mt-1">
                              Resolved {new Date(noteReviewData.note.corrections_resolved_at).toLocaleString()}
                              {noteReviewData.note.corrections_resolved_by_name && <> by {noteReviewData.note.corrections_resolved_by_name}</>}
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )
                  )}

                  {/* Client, Provider & Insurance Info Header */}
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-3 gap-6">
                        {/* Client Demographics */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Client Information</p>
                          {(() => {
                            const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                            const clientName = client?.full_name || `${client?.first_name || ''} ${client?.last_name || ''}`.trim() || 'Unknown Client';
                            const dob = client?.date_of_birth;
                            let dobDisplay = 'N/A';
                            if (dob) {
                              if (typeof dob === 'string' && dob.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                const [year, month, day] = dob.split('-');
                                dobDisplay = new Date(year, month - 1, day).toLocaleDateString();
                              } else {
                                dobDisplay = new Date(dob).toLocaleDateString();
                              }
                            }
                            return (
                              <>
                                <p className="font-semibold text-lg">{clientName}</p>
                                <p className="text-sm text-gray-600 mt-1">DOB: {dobDisplay}</p>
                                <p className="text-sm text-gray-600">
                                  MRN: {(() => {
                                    const raw = client?.mrn;
                                    if (!raw) return 'N/A';
                                    // Strip a redundant "MRN-" or "MRN " prefix on the value so the
                                    // label "MRN:" doesn't duplicate it. (Some legacy clients have
                                    // values like "MRN-000001"; the prefix is meaningless here.)
                                    return String(raw).replace(/^\s*MRN[-\s_]*/i, '');
                                  })()}
                                </p>
                              </>
                            );
                          })()}
                        </div>

                        {/* Provider Info */}
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Provider</p>
                          {(() => {
                            const prov = users.find(u => u.id === noteReviewData.note.provider_id) || providers.find(p => p.id === noteReviewData.note.provider_id);
                            const fullName = prov?.full_name || 'Unknown';
                            const creds = prov?.credentials || '';

                            // License status — billers want a quick "is this provider
                            // independently licensed?" signal. Use the same independent-cred
                            // list the rest of the app uses (drives Box 24J on the claim).
                            const licenseStatus = (prov?.license_status || '').toLowerCase();
                            const INDEPENDENT_CREDS = ['LICSW','LCSW','LMHC','LMFT','LPC','LPCC','LCPC','LISW','LPCMH','PSYD','PHD','EDD','MD','DO','NP','CNP','PMHNP','FNP','PNP','APRN','DNP','PA','PAC'];
                            const provCredTokens = (creds || '').toUpperCase().replace(/[.,;\-]/g, ' ').split(/\s+/).filter(Boolean);
                            const isLicensed = licenseStatus === 'licensed' || prov?.is_supervisor || prov?.role?.toLowerCase() === 'supervisor' || provCredTokens.some(t => INDEPENDENT_CREDS.includes(t));
                            const licenseLabel = isLicensed ? 'Licensed' : 'Unlicensed';
                            const licenseColor = isLicensed ? 'text-emerald-700' : 'text-amber-700';

                            return (
                              <>
                                <p className="font-semibold text-lg">
                                  {fullName}{creds ? `, ${creds}` : ''}
                                </p>
                                <p className={`text-sm mt-1 ${licenseColor}`} title={isLicensed ? 'Provider can bill independently under own NPI' : 'Provider needs supervisor co-signature / supervisory billing'}>
                                  {licenseLabel}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  NPI: {prov?.npi || 'N/A'}
                                </p>
                                {prov?.billing_credential && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Billing Credential: <span className="font-mono font-semibold">{prov.billing_credential}</span>
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Insurance Information */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-gray-500 uppercase font-semibold">Insurance & Eligibility</p>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-600" data-testid="manage-insurance-btn"
                              onClick={() => {
                                const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                                if (client) {
                                  setManagingInsuranceForClient(client);
                                  setShowInsuranceManagement(true);
                                } else {
                                  toast.error('Client not found');
                                }
                              }}>
                              <Pencil className="h-3 w-3 mr-1" /> Manage
                            </Button>
                          </div>
                          {(() => {
                            const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                            const allInsurances = getClientAllInsurances(client, insurances);
                            
                            if (allInsurances.length === 0) {
                              return (
                                <div className="text-sm text-emerald-700 font-semibold">
                                  Self Pay
                                </div>
                              );
                            }

                            // Selected insurance for this claim (default to priority 1)
                            const selectedInsIdx = noteReviewData.note._billing?.selected_insurance_idx ?? 0;
                            const selectedIns = allInsurances[selectedInsIdx] || allInsurances[0];

                            return (
                              <div className="space-y-2">
                                <select
                                  className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                                  value={selectedInsIdx}
                                  data-testid="insurance-picker"
                                  onChange={async (e) => {
                                    const idx = parseInt(e.target.value);
                                    const ins = allInsurances[idx];
                                    if (!ins) return;
                                    try {
                                      await axios.patch(`${API}/notes/${noteReviewData.note.id}/billing-fields`, {
                                        selected_insurance_idx: idx,
                                        selected_insurance: ins.company,
                                        selected_member_id: ins.member_id || '',
                                        selected_group_number: ins.group_number || '',
                                      });
                                      // Update local state so the UI reflects immediately
                                      setNoteReviewData(prev => ({
                                        ...prev,
                                        note: {
                                          ...prev.note,
                                          _billing: {
                                            ...prev.note._billing,
                                            selected_insurance_idx: idx,
                                            selected_insurance: ins.company,
                                            selected_member_id: ins.member_id || '',
                                            selected_group_number: ins.group_number || '',
                                          }
                                        }
                                      }));
                                      toast.success(`Switched to ${ins.company}`);
                                    } catch (err) {
                                      toast.error('Failed to update insurance selection');
                                    }
                                  }}
                                >
                                  {allInsurances.map((ins, idx) => (
                                    <option key={idx} value={idx}>
                                      {ins.priority === 1 ? 'Primary' : ins.priority === 2 ? 'Secondary' : ins.priority === 3 ? 'Tertiary' : `#${ins.priority}`}
                                      {' — '}{ins.company}
                                      {ins.status && ins.status !== 'active' ? ` (${ins.status})` : ''}
                                    </option>
                                  ))}
                                </select>

                                {/* Selected insurance details — Member ID + eligibility on one line for compactness */}
                                <div className="text-xs space-y-0.5 pl-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span><span className="text-gray-500">Member ID:</span> {selectedIns.member_id || 'N/A'}</span>
                                    {selectedIns.eligibility_status && (
                                      <Badge className={`text-[10px] h-4 px-1.5 ${
                                        selectedIns.eligibility_status === 'active'
                                          ? 'bg-green-100 text-green-800 border-green-300'
                                          : 'bg-yellow-50 text-yellow-800 border-yellow-300'
                                      }`}>
                                        {selectedIns.eligibility_status === 'active' ? '✓ Eligible' : `⚠ ${selectedIns.eligibility_status}`}
                                      </Badge>
                                    )}
                                  </div>
                                  {selectedIns.group_number && <p><span className="text-gray-500">Group:</span> {selectedIns.group_number}</p>}
                                </div>
                                {/* Note: "Billing as" badge moved to the Billing Information card on the right side. */}
                              </div>
                            );
                          })()}
                          {/* Quick Eligibility Check — fires a real 270 for
                              THIS note's date_of_service so billers can
                              confirm coverage on the specific DOS before
                              submitting the claim.  (Previously called the
                              legacy manual-entry endpoint /clients/{id}/
                              eligibility-check which requires insurance_
                              provider + is_eligible in the body — FastAPI
                              422'd with "field required" and the user saw
                              the "Check Eligibility from Prepare a Claim
                              is showing field required" bug.) */}
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <Button variant="outline" size="sm" className="w-full text-xs border-teal-500 text-teal-700 hover:bg-teal-50" data-testid="check-eligibility-btn"
                              onClick={async () => {
                                const clientId = noteReviewData.note.client_id;
                                const dos = noteReviewData.note.date_of_service;
                                try {
                                  toast.info(dos ? `Checking eligibility for DOS ${dos}…` : 'Checking eligibility…');
                                  const qs = dos ? `?date_of_service=${encodeURIComponent(dos)}` : '';
                                  const res = await axios.post(`${API}/eligibility/generate-270/${clientId}${qs}`);
                                  const { uploaded, realtime, has_271, upload_error, clearinghouse } = res.data || {};
                                  if (realtime && has_271) {
                                    toast.success(`Eligibility 271 received from ${clearinghouse === 'nehen' ? 'TriZetto' : 'Availity'}.`);
                                  } else if (uploaded) {
                                    const vendor = clearinghouse === 'nehen' ? 'NEHEN/TriZetto' : 'Availity';
                                    toast.success(`Eligibility check sent to ${vendor}. Response will land shortly.`);
                                  } else {
                                    toast.error(`Upload failed: ${upload_error || 'unknown error'}`);
                                  }
                                } catch (err) {
                                  if (err.response?.status === 400 && err.response?.data?.detail?.includes('No primary insurance')) {
                                    toast.error('No primary insurance on file — cannot check eligibility');
                                  } else {
                                    toast.error(err.response?.data?.detail || 'Eligibility check failed');
                                  }
                                }
                              }}>
                              <Shield className="h-3 w-3 mr-1" />
                              {noteReviewData.note.date_of_service
                                ? `Check Eligibility for ${formatDateStringForDisplay(noteReviewData.note.date_of_service)}`
                                : 'Check Eligibility'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-6">
                    {/* Left Column - Note Content */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h3 className="font-semibold text-lg">Clinical Note</h3>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-blue-600 hover:text-blue-800 p-0 h-auto"
                          data-testid="open-full-note-btn"
                          onClick={() => {
                            // Jun 2026 user request — DON'T close the
                            // Prepare Claim dialog: the note opens on top
                            // (legacy) or the dialog restores when the
                            // biller navigates back (V2 note route).
                            openNote(noteReviewData.note, true);
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Open Full Note
                        </Button>
                        {noteReviewData.appointment && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-purple-600 hover:text-purple-800 p-0 h-auto"
                            data-testid="open-linked-appointment-btn"
                            onClick={() => {
                              const apt = noteReviewData.appointment;
                              // Jun 2026 — appointment dialog stacks on
                              // top; Prepare Claim stays open underneath.
                              openAppointment(apt);
                            }}
                            title="Open the appointment linked to this note"
                          >
                            <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                            Open Appointment
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-teal-600 hover:text-teal-800 p-0 h-auto"
                          data-testid="open-client-documents-btn"
                          onClick={async () => {
                            const clientId = noteReviewData.note.client_id;
                            if (clientId) {
                              // Jun 2026 — chart opens on top; Prepare
                              // Claim stays open underneath.  The
                              // Documents tab in the client chart is
                              // internally `value="notes"` (the trigger
                              // label is "Documents" but the Radix tab
                              // value didn't get renamed).
                              handleClientClick(clientId, { initialTab: 'notes' });
                            }
                          }}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          Open Documents
                        </Button>
                        {/* Mar 2026 — User report: "frequently that's the
                            tab I want to go to" — billers want a one-click
                            jump from the Prepare-a-Claim note-review pane
                            into the client chart's Billing tab.  Same
                            close-then-open pattern as Open Appointment /
                            Open Documents above. */}
                        <Button
                          variant="link"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-800 p-0 h-auto"
                          data-testid="open-client-billing-btn"
                          onClick={() => {
                            const clientId = noteReviewData?.note?.client_id;
                            if (!clientId) return;
                            // Jun 2026 — chart opens on top; Prepare Claim
                            // stays open underneath.
                            handleClientClick(clientId, { initialTab: 'billing' });
                          }}
                          title="Open this client's chart at the Billing tab"
                        >
                          <Receipt className="h-3.5 w-3.5 mr-1" />
                          Open Billing
                        </Button>
                      </div>

                      {/* Consent affirmation lives inside the note itself now that the
                          right side renders the full note via FormFieldRenderer, so we
                          no longer need the separate consent block here. */}

                      <Card className="max-h-[600px] overflow-y-auto border-gray-200 shadow-sm">
                        <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <CardTitle className="text-sm font-semibold text-gray-900 truncate" data-testid="prep-claim-note-title">
                                {noteReviewData.note.template_name || noteReviewData.note.title || 'Clinical Note'}
                              </CardTitle>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {noteReviewData.note.doc_number && (
                                  <span className="font-mono">#{noteReviewData.note.doc_number} · </span>
                                )}
                                DOS: {formatDateStringForDisplay(noteReviewData.note.date_of_service)}
                                {noteReviewData.note.provider_signed_at && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                                    <CheckCircle className="h-3 w-3" /> Signed
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4 bg-white" data-testid="prep-claim-note-body">
                          {(() => {
                            // Parse note content into the same shape FormFieldRenderer expects.
                            let noteData = null;
                            try {
                              noteData = noteReviewData.note.content
                                ? (typeof noteReviewData.note.content === 'string'
                                    ? JSON.parse(noteReviewData.note.content)
                                    : noteReviewData.note.content)
                                : null;
                            } catch (parseErr) {
                              noteData = null;
                            }

                            if (!noteData || (!noteData.fields && !noteData.sections)) {
                              // ``note.content`` may be (a) a plain text string,
                              // (b) a JSON string that parsed to an object lacking
                              // fields/sections, or (c) already an object stored
                              // that way. Only string content is safe to drop into
                              // a <span>; rendering a raw object crashes React with
                              // "Objects are not valid as a React child (found:
                              // object with keys {})". Fall back to a
                              // pretty-printed JSON dump so the biller can still
                              // see the underlying data.
                              const rawContent = noteReviewData.note.content;
                              let displayContent = null;
                              if (typeof rawContent === 'string' && rawContent.trim()) {
                                displayContent = rawContent;
                              } else if (rawContent && typeof rawContent === 'object') {
                                try {
                                  displayContent = JSON.stringify(rawContent, null, 2);
                                } catch (_) {
                                  displayContent = null;
                                }
                              }
                              return (
                                <p className="text-sm text-gray-500 italic">
                                  {displayContent ? (
                                    <span className="whitespace-pre-wrap not-italic text-gray-700 font-mono text-xs">{displayContent}</span>
                                  ) : 'No note content available'}
                                </p>
                              );
                            }

                            // Flatten field values into the form-data map the renderer reads.
                            const formDataForRenderer = {};
                            if (noteData.sections) {
                              noteData.sections.forEach(section => {
                                (section.fields || []).forEach(field => {
                                  if (field.id && field.value !== undefined) {
                                    formDataForRenderer[field.id] = field.value;
                                  }
                                });
                              });
                            } else if (noteData.fields) {
                              noteData.fields.forEach(field => {
                                if (field.id && field.value !== undefined) {
                                  formDataForRenderer[field.id] = field.value;
                                }
                              });
                            }

                            const noteClient = noteReviewData.client
                              || allClientsForLookup?.find(c => c.id === noteReviewData.note.client_id)
                              || findClientById(noteReviewData.note.client_id);

                            // Context mirrors the Client Documents View-mode renderer so the
                            // Prepare-Claim preview matches the "real" note dialog visually.
                            const prepClaimViewContext = {
                              selectedClient: noteClient,
                              clients: allClientsForLookup,
                              selectedNoteClients: noteReviewData.note.client_ids || [noteReviewData.note.client_id].filter(Boolean),
                              clientDiagnoses: [],
                              clientMedications: [],
                              clientVitals: [],
                              user: user,
                              users: users,
                              appointments: appointments,
                              newNoteFormData: formDataForRenderer,
                              formTemplates: formTemplates,
                              linkedGoals: noteReviewData.note.linked_goals || [],
                              treatmentPlanGoals: [],
                              availablePANs: [],
                              telehealthConsent: noteReviewData.note.telehealth_consent || {},
                              setTelehealthConsent: () => {},
                              organizations: organizations,
                              posCodes: organizations?.[0]?.pos_codes || [],
                              serviceTypes: serviceTypes,
                              locations: locations,
                              providers: users?.filter(u => u.is_provider) || [],
                              allGroups: clientGroups || [],
                              isGroupNote: noteReviewData.note.is_group_note || false,
                              groupMembers: [],
                              groupAttendance: {},
                              groupMemberNotes: {},
                              selectedNote: noteReviewData.note,
                              isEditMode: false,
                              disabled: true,
                              mode: 'view',
                              handleFieldChange: () => {},
                              setNewNoteFormData: () => {},
                              setSelectedClient: () => {},
                              setShowDiagnosisForm: () => {},
                              setShowMedicationForm: () => {},
                              setEditingMedication: () => {},
                              setShowLinkGoalsDialog: () => {},
                              fetchTreatmentPlanGoals: () => {},
                              setLinkedGoals: () => {},
                              loadedSignatures: loadedSignatures,
                              setLoadedSignatures: setLoadedSignatures,
                              getSignatureUrl: getSignatureUrl,
                              currentUser: user,
                            };

                            const fieldsForRenderer = noteData.sections
                              ? null
                              : (noteData.fields || []);
                            const sectionsForRenderer = noteData.sections || null;

                            return (
                              <div className="space-y-4">
                                <FormFieldRenderer
                                  key={`prep-claim-${noteReviewData.note.id}`}
                                  fields={fieldsForRenderer}
                                  sections={sectionsForRenderer}
                                  contextValue={prepClaimViewContext}
                                  mode="view"
                                  disabled={true}
                                />
                              </div>
                            );
                          })()}
                        </CardContent>
                      </Card>

                      {/* Signatures Section */}
                      <Card className="bg-green-50 border-green-200">
                        <CardHeader>
                          <CardTitle className="text-sm flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            Signatures
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {noteReviewData.note.provider_signed_at ? (
                              <div className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <div>
                                  <p className="font-medium">Provider Signed</p>
                                  <p className="text-xs text-gray-600">
                                    {(users.find(u => u.id === noteReviewData.note.provider_signed_by) || providers.find(p => p.id === noteReviewData.note.provider_signed_by))?.full_name || 'Unknown'}
                                    {' - '}
                                    {new Date(noteReviewData.note.provider_signed_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm text-orange-600">
                                <AlertCircle className="h-4 w-4" />
                                <p>Provider signature missing</p>
                              </div>
                            )}
                            
                            {noteReviewData.note.supervisor_signed_at ? (
                              <div className="flex items-center gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <div>
                                  <p className="font-medium">Supervisor Signed</p>
                                  <p className="text-xs text-gray-600">
                                    {(users.find(u => u.id === noteReviewData.note.supervisor_signed_by) || providers.find(p => p.id === noteReviewData.note.supervisor_signed_by))?.full_name || 'Unknown'}
                                    {' - '}
                                    {new Date(noteReviewData.note.supervisor_signed_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ) : noteReviewData.note.billable ? (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <XCircle className="h-4 w-4" />
                                <p>No supervisor signature required</p>
                              </div>
                            ) : null}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Right Column - Service & Billing Details */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg border-b pb-2">Service & Claim Details</h3>
                      
                      {/* Service Line Preview */}
                      {(() => {
                        const provider = users.find(u => u.id === noteReviewData.note.provider_id) || providers.find(p => p.id === noteReviewData.note.provider_id);
                        const apt = noteReviewData.appointment;
                        const note = noteReviewData.note;
                        const tc = note.telehealth_consent || {};
                        
                        const client = noteReviewData.client || findClientById(note.client_id);
                        const allIns = getClientAllInsurances(client, insurances);
                        const primaryIns = allIns.find(i => i.priority === 1) || allIns[0];
                        const pbt = primaryIns?.payer_billing_types || [];
                        const hasSupervisor = !!(provider?.supervisor_id || provider?.assigned_supervisor);
                        const supervisorId = provider?.supervisor_id || provider?.assigned_supervisor;
                        const supervisor = hasSupervisor ? (users.find(u => u.id === supervisorId) || providers.find(p => p.id === supervisorId)) : null;
                        
                        // Check if provider can bill independently (aligned with routing rules)
                        // Uses license_status (primary) and credential parsing (fallback)
                        const licenseStatus = (provider?.license_status || '').toLowerCase();
                        const INDEPENDENT_CREDS = ['LICSW','LCSW','LMHC','LMFT','LPC','LPCC','LCPC','LISW','LPCMH','PSYD','PHD','EDD','MD','DO','NP','CNP','PMHNP','FNP','PNP','APRN','DNP','PA','PAC'];
                        const provCredTokens = (provider?.credentials || '').toUpperCase().replace(/[.,;\-]/g, ' ').split(/\s+/);
                        const isIndependentlyLicensed = licenseStatus === 'licensed' || provider?.is_supervisor || provider?.role?.toLowerCase() === 'supervisor' || provCredTokens.some(t => INDEPENDENT_CREDS.includes(t));
                        
                        let billingType = 'Individual';
                        if (pbt.includes('facility')) billingType = 'Facility';
                        else if (pbt.includes('medicare') && provider?.medicare_approved) billingType = 'Individual';
                        else if (pbt.includes('medicare') && !provider?.medicare_approved && hasSupervisor) billingType = 'Supervisor';
                        else if ((pbt.includes('supervisory') && !isIndependentlyLicensed) || (hasSupervisor && !pbt.includes('individual') && !isIndependentlyLicensed)) billingType = 'Supervisor';
                        
                        const cptCodeId = note._billing?.cpt_code_id || note.cpt_code_id || apt?.cpt_code_id || null;
                        const cptCode = note._billing?.cpt_code || note.cpt_code || (apt?.cpt_code_id ? (cptCodes?.find(c => c.id === apt.cpt_code_id)?.code) : null) || (apt?.service_type ? apt.service_type.split(/\s*-\s*/)[0].trim() : null) || null;
                        // Resolve the *specific* CPT doc. Multiple active CPT rows can share a code (e.g. "90834 — SOA Discharge"
                        // vs "90834 — Outpatient Therapy 35-53 min"); prefer cpt_code_id lookup so we don't pick the wrong row.
                        const resolvedCpt = (cptCodeId && cptCodes?.find(c => c.id === cptCodeId))
                          || (cptCode ? cptCodes?.find(c => c.code === cptCode) : null);
                        const cptDescription = resolvedCpt?.description || '';
                        // Jun 2026 — modality resolution aligned with the backend
                        // (backend/core/modality_sync.py aliases + the wire
                        // generator's canon order: consent → note → appointment,
                        // accepting '93'/'95' modifier values as inputs too).
                        const canonModality = (...candidates) => {
                          for (let v of candidates) {
                            v = String(v || '').trim().toLowerCase();
                            if (['93', 'audio_only', 'audio-only', 'phone', 'telephone'].includes(v)) return 'audio_only';
                            if (['95', 'audio_visual', 'audio-visual', 'audio_video', 'video', 'av'].includes(v)) return 'audio_visual';
                          }
                          return null;
                        };
                        const resolvedModality = canonModality(
                          tc.modality, tc.modifier, note.modality, note.telehealth_modality,
                          apt?.telehealth_modality, apt?.telehealth_modifier,
                        );
                        const isTelehealth = !!resolvedModality;
                        // Modifier source of truth (priority order):
                        //   1. previewModifiers — resolved by POST /claims/preview-from-note
                        //      when the dialog opened (the exact code path that will
                        //      build the claim's service line).
                        //   2. note._billing.modifiers — biller edit, re-resolved below.
                        //   3. auto-inferred credential + telehealth modifiers.
                        const previewMods = Array.isArray(noteReviewData.previewModifiers) ? noteReviewData.previewModifiers : null;
                        const savedModifiers = Array.isArray(note._billing?.modifiers) ? note._billing.modifiers : null;
                        let mods = previewMods ? [...previewMods] : savedModifiers ? [...savedModifiers] : [];
                        // Re-resolve stale telehealth modifiers: if consent/modality
                        // changed AFTER _billing.modifiers was saved, swap the stored
                        // 93/95 for the one modality dictates so a previously-saved
                        // 95 can't persist (display-level; the backend applies the
                        // same mutex at claim-creation time).
                        if (resolvedModality) {
                            const expectedTh = resolvedModality === 'audio_only' ? '93' : '95';
                            if (mods.some(m => ['93','95'].includes(String(m).trim()) && String(m).trim() !== expectedTh)) {
                                mods = mods.filter(m => !['93','95'].includes(String(m).trim()));
                                mods.push(expectedTh);
                            }
                        } else if (mods.includes('93') && mods.includes('95')) {
                            // Modality silent + both persisted (legacy garbage) →
                            // legacy 95 default, matching the backend mutex.
                            mods = mods.filter(m => !['93','95'].includes(String(m).trim()));
                            mods.push('95');
                        }
                        const inferBillingCredential = (creds) => {
                          if (!creds) return '';
                          const tokens = creds.toUpperCase().replace(/[.,;]/g, ' ').split(/\s+/);
                          const sa = ['NP','CNP','PMHNP','FNP','PNP','APRN','DNP','PA','PAC','PA-C'];
                          const ah = ['PSYD','PHD','EDD'];
                          const ho = ['MSW','MA','MS','MED','LMFT','LPC','LCSW','LICSW','LMHC','LPCMH','LCPC','LPCC','LAMFT','LISW','MFT','LADC','LADCI','LADCII','CADC'];
                          const hn = ['BA','BS','BSW','BBA'];
                          for (const t of tokens) { if (sa.includes(t)) return 'SA'; }
                          for (const t of tokens) { if (ah.includes(t)) return 'AH'; }
                          for (const t of tokens) { if (ho.includes(t)) return 'HO'; }
                          for (const t of tokens) { if (hn.includes(t)) return 'HN'; }
                          return '';
                        };
                        // When the note bills under a supervisor, the credential modifier
                        // must reflect the SUPERVISOR's credentials (not the rendering
                        // provider's), per the same rule applied server-side in
                        // ``get_billing_modifiers`` (server.py:35681).  Otherwise an unlicensed
                        // intern's credentials would surface as the billed modifier — wrong.
                        const billingCredentialed = (billingType && billingType.toLowerCase() === 'supervisor' && supervisor) ? supervisor : provider;
                        const billingCred = billingCredentialed?.billing_credential
                          || inferBillingCredential(billingCredentialed?.credentials);
                        // Determine telehealth from MODALITY (clinical truth), not
                        // google_meet_link (which only reflects scheduling tooling and
                        // breaks for providers using Doxy/Zoom/custom URLs).
                        // `isTelehealth`/`resolvedModality` computed above (canon order).
                        // Always-on credential modifier visibility:
                        //   The credential modifier (HO / AH / HN / SA) is determined by the
                        //   provider's role and doesn't change per-claim, so we surface it on
                        //   the service line whenever it's missing — even when the backend
                        //   already pre-populated _billing.modifiers (e.g. with just ['95']).
                        //   Same for the telehealth modifier when the appt is telehealth.
                        //   Bug-fix: previously this auto-merge ran ONLY when no saved
                        //   modifiers existed at all, which silently hid HO on every claim
                        //   that had been touched once (because notes-ready writes
                        //   _billing.modifiers eagerly).
                        const hasCredMod = mods.some(m => ['AH','HO','HN','SA'].includes(String(m).toUpperCase()));
                        if (!hasCredMod && billingCred && ['AH','HO','HN','SA'].includes(billingCred)) {
                            mods.push(billingCred);
                        }
                        const hasTelehealthMod = mods.some(m => ['93','95'].includes(String(m).trim()));
                        if (!hasTelehealthMod && resolvedModality) {
                            // Modality (consent → note → appointment, alias-normalized)
                            // maps directly to the modifier.  No hardcoded '95'
                            // fallback: when modality can't be resolved we add
                            // nothing, matching enforce_telehealth_modifier_mutex.
                            mods.push(resolvedModality === 'audio_only' ? '93' : '95');
                        }
                        
                        // POS resolution priority:
                        //   1. note._billing.pos     ← biller-overridden POS (this dialog writes here)
                        //   2. apt.pos_code          ← appointment-level POS
                        //   3. tc.pos_code           ← consent affirmation legacy field
                        //   4. Modality-only fallback → '10' (Telehealth-Home, post-2022 default)
                        // Pre-2022 plans still used '02' (Telehealth other-than-home); we default
                        // to '10' to match modern payer expectations.  Mirrors the backend
                        // `resolve_pos_code` function in routes/billing.py.
                        // BUG (fixed May 2026): the literal fallback used to be '02' here while
                        // the backend list resolver fell through to '11' — Prepare-a-Claim
                        // showed POS 10/02 (correct) but the Billing list showed POS 11.
                        const posCode = note._billing?.pos || apt?.pos_code || tc.pos_code || (isTelehealth ? '10' : null);
                        const posDesc = posCode ? (tc.pos_description || (getEffectivePosCodes(organizations, posCodes)).find(p => p.code === posCode)?.description || '') : '';
                        const units = note.units || apt?.units || 1;
                        // Jun 2026 — display DOS/time resolve APPOINTMENT-FIRST
                        // (then note fallback) to match backend submission logic.
                        const dos = apt?.start_time ? parseUTCTimestamp(apt.start_time)?.toLocaleDateString() : note.date_of_service ? formatDateStringForDisplay(note.date_of_service) : null;
                        const startTime = apt?.start_time ? parseUTCTimestamp(apt.start_time)?.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : note.start_time ? formatTimeTo12Hour(note.start_time) : null;
                        const endTime = apt?.end_time ? parseUTCTimestamp(apt.end_time)?.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : note.end_time ? formatTimeTo12Hour(note.end_time) : null;
                        
                        return (
                          <Card data-testid="service-line-preview">
                            <CardContent className="pt-4 pb-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-500">Claim Service Line</p>
                                {!editingServiceLine ? (
                                  <Button variant="ghost" size="sm" className="h-6 px-2 text-blue-600" data-testid="edit-service-line-btn"
                                    onClick={() => {
                                      setEditingServiceLine(true);
                                      // Seed date + time-of-day in the form.
                                      // The appointment's start/end are ISO UTC; convert to local
                                      // HH:MM for the <input type="time"> fields. If there is no
                                      // appointment, fall back to whatever the note has (rare).
                                      const isoStart = apt?.start_time || note.start_time || null;
                                      const isoEnd = apt?.end_time || note.end_time || null;
                                      const toLocalHHMM = (iso) => {
                                        if (!iso) return '';
                                        try {
                                          const d = new Date(iso);
                                          if (isNaN(d.getTime())) return '';
                                          const h = String(d.getHours()).padStart(2, '0');
                                          const m = String(d.getMinutes()).padStart(2, '0');
                                          return `${h}:${m}`;
                                        } catch (_) { return ''; }
                                      };
                                      setServiceLineEdits({
                                        cpt_code_id: resolvedCpt?.id || cptCodeId || '',
                                        cpt_code: cptCode || '',
                                        modifiers: [...mods],
                                        // Raw text the user is typing (so they can type commas
                                        // and trailing spaces freely). We split into the modifiers
                                        // array only on save.
                                        modifiers_raw: mods.join(', '),
                                        pos: posCode || '',
                                        // Modality only meaningful for telehealth POS (02/10).
                                        // Seed from existing modifiers so reopening the editor
                                        // preserves the current state.
                                        modality: mods.includes('93') ? 'audio_only'
                                          : mods.includes('95') ? 'audio_visual'
                                          : '',
                                        units: units || 1,
                                        // Charges is the TOTAL for the service line (CMS-1500 box 24F),
                                        // i.e. base_price × units. If a saved value already exists,
                                        // honor that (biller may have manually overridden); otherwise
                                        // seed with base_price × units so multi-unit codes don't show
                                        // just the per-unit base.
                                        charges: note._billing?.charges
                                          || ((resolvedCpt?.base_price || 0) * (units || 1))
                                          || 0,
                                        date_of_service: note.date_of_service || (isoStart ? isoStart.slice(0, 10) : ''),
                                        start_time: toLocalHHMM(isoStart),
                                        end_time: toLocalHHMM(isoEnd),
                                        _initial_date_of_service: note.date_of_service || (isoStart ? isoStart.slice(0, 10) : ''),
                                        _initial_start_time: toLocalHHMM(isoStart),
                                        _initial_end_time: toLocalHHMM(isoEnd),
                                      });
                                    }}>
                                    <Pencil className="h-3 w-3 mr-1" /> Edit
                                  </Button>
                                ) : (
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-gray-500" onClick={() => setEditingServiceLine(false)}>Cancel</Button>
                                    <Button size="sm" className="h-6 px-2 bg-teal-600 hover:bg-teal-700 text-white" disabled={savingServiceLine} data-testid="save-service-line-btn"
                                      onClick={async () => {
                                        setSavingServiceLine(true);
                                        try {
                                          // Always re-parse modifiers from the raw input at save time
                                          // so a user who's mid-typing (e.g. "HO, 95,") hits Save and
                                          // gets ['HO','95'] instead of whatever was last committed.
                                          const finalModifiers = (serviceLineEdits.modifiers_raw ?? (serviceLineEdits.modifiers || []).join(', '))
                                            .split(',')
                                            .map(m => m.trim())
                                            .filter(Boolean);
                                          const payload = {};
                                          // Only mark CPT as changed if the user actually picked a different
                                          // row in the dropdown. We compare against the dropdown's loaded
                                          // snapshot, NOT against the outer closure vars — those can be stale
                                          // (null vs auto-resolved first-match id) and falsely trigger a sync.
                                          const cptIdChanged = !!serviceLineEdits.cpt_code_id
                                            && serviceLineEdits.cpt_code_id !== serviceLineEdits._initial_cpt_code_id;
                                          const cptCodeChanged = serviceLineEdits.cpt_code !== serviceLineEdits._initial_cpt_code;
                                          if (cptIdChanged || cptCodeChanged) {
                                            if (serviceLineEdits.cpt_code_id) payload.cpt_code_id = serviceLineEdits.cpt_code_id;
                                            if (serviceLineEdits.cpt_code) payload.cpt_code = serviceLineEdits.cpt_code;
                                          }
                                          if (JSON.stringify(finalModifiers) !== JSON.stringify(mods)) payload.modifiers = finalModifiers;
                                          if (serviceLineEdits.pos !== posCode) payload.pos = serviceLineEdits.pos;
                                          if (serviceLineEdits.units !== units) payload.units = parseInt(serviceLineEdits.units) || 1;
                                          if (serviceLineEdits.charges !== (note._billing?.charges || 0)) payload.charges = parseFloat(serviceLineEdits.charges) || 0;

                                          // DOS + time-of-day — rebuild ISO strings using the user's local
                                          // tz so the appointment lands on the correct calendar slot. Send
                                          // date_of_service as YYYY-MM-DD and start_time_iso/end_time_iso
                                          // as full UTC ISO so backend can store them verbatim.
                                          const dosChanged = !!serviceLineEdits.date_of_service
                                            && serviceLineEdits.date_of_service !== serviceLineEdits._initial_date_of_service;
                                          const startChanged = (serviceLineEdits.start_time || '') !== (serviceLineEdits._initial_start_time || '');
                                          const endChanged = (serviceLineEdits.end_time || '') !== (serviceLineEdits._initial_end_time || '');
                                          if (dosChanged) payload.date_of_service = serviceLineEdits.date_of_service;
                                          const dosForCombine = serviceLineEdits.date_of_service
                                            || serviceLineEdits._initial_date_of_service;
                                          if ((startChanged || (dosChanged && serviceLineEdits.start_time)) && dosForCombine && serviceLineEdits.start_time) {
                                            try {
                                              const iso = new Date(`${dosForCombine}T${serviceLineEdits.start_time}:00`).toISOString();
                                              payload.start_time_iso = iso;
                                            } catch (_) { /* ignore invalid date */ }
                                          }
                                          if ((endChanged || (dosChanged && serviceLineEdits.end_time)) && dosForCombine && serviceLineEdits.end_time) {
                                            try {
                                              const iso = new Date(`${dosForCombine}T${serviceLineEdits.end_time}:00`).toISOString();
                                              payload.end_time_iso = iso;
                                            } catch (_) { /* ignore invalid date */ }
                                          }
                                          if (Object.keys(payload).length > 0) {
                                            console.log('[ServiceLine SAVE] payload=', payload, 'cptCodeId(prev)=', cptCodeId, 'cptCode(prev)=', cptCode);
                                            const resp = await axios.patch(`${API}/notes/${note.id}/billing-fields`, payload);
                                            console.log('[ServiceLine SAVE] response=', resp.data);
                                            const syncedMsg = resp.data?.claims_synced > 0
                                              ? `Service line updated (and ${resp.data.claims_synced} claim${resp.data.claims_synced > 1 ? 's' : ''} synced)`
                                              : 'Service line updated';
                                            toast.success(syncedMsg);

                                            // Optimistic: patch the in-memory billing list so the user sees
                                            // the new CPT/modifiers on the Notes Ready for Billing row right away.
                                            const newBilling = resp.data?.billing || {};
                                            setNotesReadyForBilling(prev => prev.map(n => (
                                              n.id === note.id
                                                ? { ...n, _billing: { ...(n._billing || {}), ...newBilling } }
                                                : n
                                            )));

                                            // Optimistic: patch the appointment in the shared appointments
                                            // list so the calendar/schedule reflects the new modality +
                                            // telehealth_modifier right away.
                                            if (note.appointment_id) {
                                              const mods = Array.isArray(newBilling.modifiers) ? newBilling.modifiers.map(m => String(m).toUpperCase()) : [];
                                              let telehealth_modality = null;
                                              let telehealth_modifier = null;
                                              if (mods.includes('93')) {
                                                telehealth_modality = 'audio_only';
                                                telehealth_modifier = '93';
                                              } else if (mods.includes('95')) {
                                                telehealth_modality = 'audio_visual';
                                                telehealth_modifier = '95';
                                              }
                                              setAppointments(prev => prev.map(a => {
                                                if (a.id !== note.appointment_id) return a;
                                                const patched = { ...a, telehealth_modality, telehealth_modifier };
                                                if (newBilling.pos) patched.pos_code = newBilling.pos;
                                                if (newBilling.units) patched.units = newBilling.units;
                                                if (newBilling.cpt_code_id) patched.cpt_code_id = newBilling.cpt_code_id;
                                                // DOS / time-of-day optimistic sync — mirrors the backend,
                                                // which already wrote these fields onto the appointment.
                                                if (payload.start_time_iso) patched.start_time = payload.start_time_iso;
                                                if (payload.end_time_iso) patched.end_time = payload.end_time_iso;
                                                return patched;
                                              }));
                                            }

                                            // Refetch the authoritative note state (source of truth)
                                            fetchNoteForReview(note.id);

                                            // If the user had a preview open (dry-run or 837P for an existing
                                            // claim), re-fetch it so the modifiers/CPT/POS in the preview
                                            // reflect the edits immediately.
                                            if (billingPreviewClaimId) {
                                              try {
                                                const prevResp = await axios.get(`${API}/claims/${billingPreviewClaimId}/preview-837p`);
                                                setDryRunPreviewData(prevResp.data);
                                              } catch (_) { /* non-fatal */ }
                                            } else if (dryRunPreviewData) {
                                              try {
                                                const prevResp = await axios.post(`${API}/claims/preview-from-note/${note.id}`);
                                                setDryRunPreviewData(prevResp.data);
                                              } catch (_) { /* non-fatal */ }
                                            }
                                          }
                                          setEditingServiceLine(false);
                                        } catch (err) {
                                          // Scheduling conflict (409) — surface the human-readable
                                          // message from check_scheduling_conflicts so billers see
                                          // exactly which client/time is blocking the move.
                                          const detail = err.response?.data?.detail;
                                          if (err.response?.status === 409 && detail?.message) {
                                            toast.error(detail.message, { duration: 8000 });
                                          } else if (typeof detail === 'string') {
                                            toast.error(detail);
                                          } else {
                                            toast.error('Failed to update');
                                          }
                                        } finally {
                                          setSavingServiceLine(false);
                                        }
                                      }}>
                                      {savingServiceLine ? 'Saving...' : 'Save'}
                                    </Button>
                                  </div>
                                )}
                              </div>

                              {!editingServiceLine ? (
                                <>
                                  {/* Service location (Loop 2310C / Box 32) — resolved by the
                                      backend preview so this always matches what the claim
                                      will actually transmit. Jun 2026. */}
                                  {noteReviewData.previewServiceLocation && (
                                    <div className="text-xs text-gray-600 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="service-line-location">
                                      <span className="text-gray-500 inline-flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> Service Location:
                                      </span>
                                      {/* Jun 2026 — short location name (display_name) for the
                                          UI; the full org `name`/address still feed the 837P. */}
                                      <span className="font-medium">{noteReviewData.previewServiceLocation.display_name || noteReviewData.previewServiceLocation.name}</span>
                                      <span className="text-[10px] text-gray-400 italic">
                                        {noteReviewData.previewServiceLocation.source === 'note' ? 'from note location'
                                          : noteReviewData.previewServiceLocation.source === 'appointment' ? 'from appointment location'
                                          : noteReviewData.previewServiceLocation.source === 'provider' ? "from provider's location"
                                          : 'organization default'}
                                      </span>
                                    </div>
                                  )}
                                  {/* Read-only display */}
                                  <div className="font-mono font-bold text-base tracking-wide flex flex-wrap items-center gap-1">
                                    <TooltipProvider delayDuration={200}>
                                      {cptCode ? (
                                        <UITooltip>
                                          <TooltipTrigger asChild>
                                            <span className="cursor-help underline decoration-dotted decoration-gray-400">{cptCode}</span>
                                          </TooltipTrigger>
                                          <TooltipContent><p>{cptCode} — {cptDescription || 'CPT Code'}</p></TooltipContent>
                                        </UITooltip>
                                      ) : (
                                        <span className="text-orange-500">CPT missing</span>
                                      )}
                                      {mods.map((m, i) => {
                                        // Each modifier shows an explanatory tooltip including
                                        // WHY it was applied (e.g., "auto from MSW credentials")
                                        // so billers can verify the right code is being claimed
                                        // without opening the provider record.
                                        const isCredMod = ['HO','AH','HN','SA'].includes(m);
                                        const isTelehealthMod = ['93','95','GT'].includes(m);
                                        const credMeaning = {
                                          'HO': "Master's level provider",
                                          'AH': "Doctorate level provider",
                                          'HN': "Bachelor's level provider",
                                          'SA': "Advanced practice provider",
                                        };
                                        const teleMeaning = {
                                          '95': "Synchronous (audio + video) telehealth",
                                          '93': "Audio-only telehealth",
                                          'GT': "Telehealth via interactive audio/video",
                                        };
                                        let tipBody = m;
                                        if (isCredMod) {
                                          const credSrc = billingCredentialed?.credentials || billingCredentialed?.billing_credential || '';
                                          const usingSupervisor = billingCredentialed && supervisor && billingCredentialed.id === supervisor.id;
                                          tipBody = `${m} — ${credMeaning[m]}`
                                            + (credSrc ? ` · auto from ${usingSupervisor ? 'supervisor ' : ''}credentials: ${credSrc}` : '')
                                            + (usingSupervisor ? ` (billed under ${supervisor.full_name})` : '');
                                        } else if (isTelehealthMod) {
                                          tipBody = `${m} — ${teleMeaning[m]}`
                                            + (apt?.google_meet_link ? ' · auto: appointment has Google Meet link' : '')
                                            + (apt?.telehealth_modality ? ` · modality: ${apt.telehealth_modality}` : '');
                                        }
                                        return (
                                          <UITooltip key={i}>
                                            <TooltipTrigger asChild>
                                              <span className="cursor-help underline decoration-dotted decoration-gray-400" data-testid={`service-line-modifier-${m}`}>{m}</span>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs"><p>{tipBody}</p></TooltipContent>
                                          </UITooltip>
                                        );
                                      })}
                                      {posCode && (
                                        <>
                                          <span>POS</span>
                                          <UITooltip>
                                            <TooltipTrigger asChild>
                                              <span className="cursor-help underline decoration-dotted decoration-gray-400">{posCode}</span>
                                            </TooltipTrigger>
                                            <TooltipContent><p>POS {posCode}{posDesc ? ` — ${posDesc}` : ''}</p></TooltipContent>
                                          </UITooltip>
                                        </>
                                      )}
                                      <span>UNIT(S): {units}</span>
                                      {note._billing?.charges > 0 && (
                                        <span className="text-green-700 ml-1">${Number(note._billing.charges).toFixed(2)}</span>
                                      )}
                                    </TooltipProvider>
                                  </div>
                                </>
                              ) : (
                                /* Edit mode */
                                <div className="space-y-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
                                  <div className="grid grid-cols-3 gap-2">
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">CPT Code</label>
                                      <select className="w-full border rounded px-2 py-1 text-sm font-mono" value={serviceLineEdits.cpt_code_id || ''}
                                        onChange={(e) => {
                                          const id = e.target.value;
                                          const cptDoc = cptCodes?.find(c => c.id === id);
                                          setServiceLineEdits(prev => ({
                                            ...prev,
                                            cpt_code_id: id,
                                            cpt_code: cptDoc?.code || '',
                                            // Recompute total charges = base_price × units when CPT changes.
                                            charges: cptDoc?.base_price
                                              ? cptDoc.base_price * (prev.units || 1)
                                              : prev.charges,
                                          }));
                                        }}>
                                        <option value="">Select CPT</option>
                                        {cptCodes?.filter(c => c.is_active !== false).map(c => (
                                          <option key={c.id} value={c.id}>{c.code} - {c.description}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">POS</label>
                                      <select
                                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                                        value={serviceLineEdits.pos}
                                        data-testid="service-line-pos-select"
                                        onChange={(e) => {
                                          const newPos = e.target.value;
                                          // Telehealth POS triggers modality + modifier sync;
                                          // in-person POS strips telehealth modifiers (95/93)
                                          // and clears modality.
                                          const isTelehealth = ['02', '10'].includes(newPos);
                                          setServiceLineEdits(prev => {
                                            // Parse current modifiers from raw string so we
                                            // operate on what the user can actually see.
                                            const currentRaw = prev.modifiers_raw ?? (prev.modifiers || []).join(', ');
                                            const tokens = currentRaw
                                              .split(',')
                                              .map(m => m.trim().toUpperCase())
                                              .filter(Boolean);
                                            // Remove existing telehealth modifiers; we'll re-add
                                            // the right one based on POS + modality.
                                            const stripped = tokens.filter(t => t !== '93' && t !== '95');

                                            let newModality = prev.modality || '';
                                            let nextTokens = stripped;
                                            if (isTelehealth) {
                                              // Default to audio_visual (95) if no modality set.
                                              if (!newModality) newModality = 'audio_visual';
                                              nextTokens = [
                                                ...stripped,
                                                newModality === 'audio_only' ? '93' : '95',
                                              ];
                                            } else {
                                              // In-person — clear modality.
                                              newModality = '';
                                            }
                                            return {
                                              ...prev,
                                              pos: newPos,
                                              modality: newModality,
                                              modifiers: nextTokens,
                                              modifiers_raw: nextTokens.join(', '),
                                            };
                                          });
                                        }}
                                      >
                                        <option value="">Select</option>
                                        {(getEffectivePosCodes(organizations, posCodes) || []).map(p => (
                                          <option key={p.code} value={p.code}>{p.code} - {p.description}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Units</label>
                                        <input type="number" min="1" className="w-full border rounded px-2 py-1 text-sm font-mono" value={serviceLineEdits.units}
                                          onChange={(e) => setServiceLineEdits(prev => {
                                            const newUnits = parseInt(e.target.value) || 1;
                                            // Auto-recompute total charges = base_price × units.
                                            // Use the currently selected CPT's base_price as the per-unit
                                            // price. If the biller has manually overridden charges (i.e.
                                            // current charges != prevUnits × base_price), preserve their
                                            // intent only when base_price is unknown — otherwise units
                                            // edits should always update the total to keep the math right.
                                            const cptDoc = cptCodes?.find(c => c.id === prev.cpt_code_id);
                                            const basePrice = cptDoc?.base_price || 0;
                                            return {
                                              ...prev,
                                              units: newUnits,
                                              charges: basePrice
                                                ? basePrice * newUnits
                                                : prev.charges,
                                            };
                                          })} />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-gray-600">Charges $</label>
                                        <input type="number" step="0.01" min="0" className="w-full border rounded px-2 py-1 text-sm font-mono" value={serviceLineEdits.charges}
                                          onChange={(e) => setServiceLineEdits(prev => ({ ...prev, charges: parseFloat(e.target.value) || 0 }))} />
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-gray-600">Modifiers (comma-separated)</label>
                                    <input
                                      className="w-full border rounded px-2 py-1 text-sm font-mono"
                                      value={serviceLineEdits.modifiers_raw ?? (serviceLineEdits.modifiers || []).join(', ')}
                                      data-testid="service-line-modifiers-input"
                                      onChange={(e) => {
                                        // Store the raw string the user is typing so commas
                                        // and trailing spaces work naturally. We only split
                                        // into the modifiers array on save (and on POS change).
                                        const raw = e.target.value;
                                        setServiceLineEdits(prev => ({
                                          ...prev,
                                          modifiers_raw: raw,
                                          modifiers: raw.split(',').map(m => m.trim()).filter(Boolean),
                                        }));
                                      }}
                                    />
                                    <p className="text-xs text-gray-400 mt-0.5">e.g., HO, 95 — changes here update the note and linked appointment</p>
                                  </div>
                                  {/* Modality — only meaningful for telehealth POS (02/10). Mirrors
                                      the modality selector on the calendar / note dialog so the
                                      same logic is in lock-step. Editing modality re-syncs the
                                      95/93 modifier automatically. */}
                                  {['02', '10'].includes(serviceLineEdits.pos) && (
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">Modality</label>
                                      <select
                                        className="w-full border rounded px-2 py-1 text-sm"
                                        value={serviceLineEdits.modality || 'audio_visual'}
                                        data-testid="service-line-modality-select"
                                        onChange={(e) => {
                                          const newModality = e.target.value;
                                          setServiceLineEdits(prev => {
                                            const currentRaw = prev.modifiers_raw ?? (prev.modifiers || []).join(', ');
                                            const tokens = currentRaw.split(',').map(m => m.trim().toUpperCase()).filter(Boolean);
                                            const stripped = tokens.filter(t => t !== '93' && t !== '95');
                                            const nextTokens = [
                                              ...stripped,
                                              newModality === 'audio_only' ? '93' : '95',
                                            ];
                                            return {
                                              ...prev,
                                              modality: newModality,
                                              modifiers: nextTokens,
                                              modifiers_raw: nextTokens.join(', '),
                                            };
                                          });
                                        }}
                                      >
                                        <option value="audio_visual">Audio + Video (95)</option>
                                        <option value="audio_only">Audio Only (93)</option>
                                      </select>
                                      <p className="text-xs text-gray-400 mt-0.5">Telehealth modifier auto-applied to the modifier list above.</p>
                                    </div>
                                  )}
                                  {/* DOS + time-of-day — saves to note, appointment, and the claim's
                                      service line date_from/date_to (Box 24A / DTP*472). */}
                                  <div className="grid grid-cols-3 gap-2">
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">Date of Service</label>
                                      <input
                                        type="date"
                                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                                        value={serviceLineEdits.date_of_service || ''}
                                        onChange={(e) => setServiceLineEdits(prev => ({ ...prev, date_of_service: e.target.value }))}
                                        data-testid="service-line-dos-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">Start time</label>
                                      <input
                                        type="time"
                                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                                        value={serviceLineEdits.start_time || ''}
                                        onChange={(e) => setServiceLineEdits(prev => ({ ...prev, start_time: e.target.value }))}
                                        data-testid="service-line-start-time-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-600">End time</label>
                                      <input
                                        type="time"
                                        className="w-full border rounded px-2 py-1 text-sm font-mono"
                                        value={serviceLineEdits.end_time || ''}
                                        onChange={(e) => setServiceLineEdits(prev => ({ ...prev, end_time: e.target.value }))}
                                        data-testid="service-line-end-time-input"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Contextual details */}
                              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                <div>
                                  <p className="text-gray-500 text-xs">DOS</p>
                                  <p className="font-medium">{dos || <span className="text-orange-600">Not specified</span>}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500 text-xs">Time</p>
                                  <p className="font-medium">{startTime && endTime ? `${startTime} – ${endTime}` : startTime || '—'}</p>
                                </div>
                                {/* Billing Type (Individual / Facility / Supervisor / Medicare)
                                    moved to the Billing Information card on the right side. */}
                                {isTelehealth && (
                                  <div>
                                    <p className="text-gray-500 text-xs">Modality</p>
                                    <Badge className="bg-green-600 text-white text-xs mt-0.5">
                                      <Video className="h-3 w-3 mr-1" />
                                      {tc.modality === 'audio_visual' ? 'Audio/Video' : tc.modality === 'audio_only' ? 'Audio Only' : tc.modality || 'Telehealth'}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                              {!apt && <p className="text-xs text-gray-400 border-t border-gray-100 pt-1">No linked calendar appointment — details from note header</p>}
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {/* Diagnosis Section - Tabular */}
                      {(() => {
                        const snap = noteReviewData.note.diagnosis_snapshot;
                        const liveDx = noteReviewData.clientActiveDiagnoses || [];
                        const hasSnapshot = snap && snap.diagnoses?.length > 0;
                        const hasLiveDx = liveDx.length > 0;
                        
                        if (!hasSnapshot && !hasLiveDx) return null;
                        
                        return (
                        <Card data-testid="diagnosis-section">
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Stethoscope className="h-4 w-4 text-purple-600" />
                              Diagnoses
                              {hasLiveDx && (
                                <Badge className="bg-green-100 text-green-800 text-[10px] ml-1">Active ({liveDx.length})</Badge>
                              )}
                            </CardTitle>
                            {hasSnapshot && !hasLiveDx && (
                              <p className="text-xs text-gray-500">
                                Captured at signing{snap.captured_at
                                  ? ` — ${new Date(snap.captured_at).toLocaleDateString()}`
                                  : ''}
                              </p>
                            )}
                            {hasLiveDx && (
                              <p className="text-xs text-gray-500">From client's active diagnoses</p>
                            )}
                          </CardHeader>
                          <CardContent className="pt-0">
                            <table className="w-full text-sm" data-testid="diagnosis-table">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-1.5 text-gray-500 font-medium w-10">#</th>
                                  <th className="text-left py-1.5 text-gray-500 font-medium w-20">Code</th>
                                  <th className="text-left py-1.5 text-gray-500 font-medium">Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hasLiveDx ? (
                                  liveDx.map((dx, idx) => (
                                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                                      <td className="py-1.5">
                                        <Badge variant={dx.is_primary ? 'default' : 'outline'} className={dx.is_primary ? 'bg-purple-600 text-white text-xs' : 'text-xs'}>
                                          {dx.is_primary ? 'P' : idx + 1}
                                        </Badge>
                                      </td>
                                      <td className="py-1.5 font-mono font-medium">{dx.diagnosis_code}</td>
                                      <td className="py-1.5 text-gray-700">{dx.diagnosis_description || dx.diagnosis_name || ''}</td>
                                    </tr>
                                  ))
                                ) : (
                                  (() => {
                                    const rows = [];
                                    if (snap.primary) {
                                      rows.push({ code: snap.primary.code, desc: snap.primary.description, label: 'P' });
                                    }
                                    if (snap.secondary) {
                                      rows.push({ code: snap.secondary.code, desc: snap.secondary.description, label: 'S' });
                                    }
                                    (snap.diagnoses || [])
                                      .filter(dx => dx.diagnosis_code !== snap.primary?.code && dx.diagnosis_code !== snap.secondary?.code)
                                      .forEach((dx) => {
                                        rows.push({ code: dx.diagnosis_code, desc: dx.diagnosis_description, label: String(rows.length + 1) });
                                      });
                                    return rows.map((row, idx) => (
                                      <tr key={idx} className="border-b border-gray-100 last:border-0">
                                        <td className="py-1.5">
                                          <Badge variant={idx === 0 ? 'default' : 'outline'} className={idx === 0 ? 'bg-purple-600 text-white text-xs' : 'text-xs'}>
                                            {row.label}
                                          </Badge>
                                        </td>
                                        <td className="py-1.5 font-mono font-medium">{row.code}</td>
                                        <td className="py-1.5 text-gray-700">{row.desc}</td>
                                      </tr>
                                    ));
                                  })()
                                )}
                              </tbody>
                            </table>
                          </CardContent>
                        </Card>
                        );
                      })()}

                      {/* Billing Info */}
                      <Card className="bg-blue-50 border-blue-200">
                        <CardHeader>
                          <CardTitle className="text-sm">Billing Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Billable:</span>
                              <Badge className={noteReviewData.note.billable ? 'bg-green-600 text-white' : 'bg-gray-600 text-white'}>
                                {noteReviewData.note.billable ? 'Yes' : 'No'}
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <Badge className="bg-blue-600 text-white">
                                {formatStatusLabel(noteReviewData.note)}
                              </Badge>
                            </div>
                            {/* Billing type — drives Box 24J/33 NPI on the rendered claim.
                                Logic mirrors backend claim_preview.py so what billers see
                                here is what the 837P will use. */}
                            {(() => {
                              const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                              const allIns = getClientAllInsurances(client, insurances);
                              if (allIns.length === 0) return null;
                              const selectedIns = allIns[noteReviewData.note._billing?.selected_insurance_idx ?? 0] || allIns[0];
                              const noteProvider = users.find(u => u.id === noteReviewData.note.provider_id) || providers.find(p => p.id === noteReviewData.note.provider_id);
                              const pbt = selectedIns?.payer_billing_types || [];
                              const hasSupervisor = !!(noteProvider?.supervisor_id || noteProvider?.assigned_supervisor);
                              const licenseStatus = (noteProvider?.license_status || '').toLowerCase();
                              const INDEPENDENT_CREDS = ['LICSW','LCSW','LMHC','LMFT','LPC','LPCC','LCPC','LISW','LPCMH','PSYD','PHD','EDD','MD','DO','NP','CNP','PMHNP','FNP','PNP','APRN','DNP','PA','PAC'];
                              const provCredTokens = (noteProvider?.credentials || '').toUpperCase().replace(/[.,;\-]/g, ' ').split(/\s+/);
                              const isIndependentlyLicensed = licenseStatus === 'licensed' || noteProvider?.is_supervisor || noteProvider?.role?.toLowerCase() === 'supervisor' || provCredTokens.some(t => INDEPENDENT_CREDS.includes(t));

                              let billingType = 'Individual';
                              let billingTypeReason = 'Provider bills under own NPI';
                              if (noteProvider?.role?.toLowerCase() === 'intern' && hasSupervisor) {
                                billingType = 'Supervisor';
                                billingTypeReason = 'Intern → claim renders supervisor as billing provider';
                              } else if (pbt.includes('facility')) {
                                billingType = 'Facility';
                                billingTypeReason = 'Payer requires facility billing (Box 33 = group NPI)';
                              } else if (pbt.includes('medicare')) {
                                if (noteProvider?.medicare_approved) {
                                  billingType = 'Medicare';
                                  billingTypeReason = 'Provider is Medicare-approved → bills under own NPI';
                                } else if (hasSupervisor) {
                                  billingType = 'Medicare (Supervisor)';
                                  billingTypeReason = 'Provider not Medicare-approved → supervisor billed';
                                } else {
                                  billingType = 'Medicare';
                                  billingTypeReason = 'No supervisor on file — verify Medicare credentialing';
                                }
                              } else if ((pbt.includes('supervisory') && !isIndependentlyLicensed) || (hasSupervisor && !pbt.includes('individual') && !isIndependentlyLicensed)) {
                                billingType = 'Supervisory';
                                billingTypeReason = 'Provider not independently licensed → supervisor billed';
                              }

                              const billingTypeColor = {
                                'Individual': 'bg-blue-100 text-blue-800 border-blue-300',
                                'Facility': 'bg-purple-100 text-purple-800 border-purple-300',
                                'Supervisory': 'bg-amber-100 text-amber-800 border-amber-300',
                                'Supervisor': 'bg-amber-100 text-amber-800 border-amber-300',
                                'Medicare': 'bg-rose-100 text-rose-800 border-rose-300',
                                'Medicare (Supervisor)': 'bg-rose-100 text-rose-800 border-rose-300',
                              }[billingType] || 'bg-gray-100 text-gray-700 border-gray-300';

                              // Resolve the actual entity name behind the billing type — i.e.
                              // who lands in Loop 2010AA / CMS-1500 Box 33 on this claim.
                              // - Facility → primary org name (single-tenant assumption)
                              // - Supervisor / Supervisory / Medicare (Supervisor) → supervisor's name
                              // - Individual / Medicare → rendering provider's name
                              let billingAsName = '';
                              if (billingType === 'Facility') {
                                billingAsName = (organizations && organizations[0]?.name) || '';
                              } else if (billingType === 'Supervisor' || billingType === 'Supervisory' || billingType === 'Medicare (Supervisor)') {
                                const supId = noteProvider?.supervisor_id || noteProvider?.assigned_supervisor;
                                const sup = supId ? users.find(u => u.id === supId) : null;
                                billingAsName = sup?.full_name || (sup ? `${sup.last_name || ''} ${sup.first_name || ''}`.trim() : '');
                              } else {
                                billingAsName = noteProvider?.full_name || (noteProvider ? `${noteProvider.last_name || ''} ${noteProvider.first_name || ''}`.trim() : '');
                              }

                              return (
                                <>
                                  <div className="flex justify-between items-center" data-testid="prep-claim-billing-type">
                                    <span className="text-gray-600">Billing as:</span>
                                    <Badge
                                      className={`text-xs font-semibold border ${billingTypeColor}`}
                                      title={billingTypeReason}
                                    >
                                      {billingType}
                                    </Badge>
                                  </div>
                                  {billingAsName && (
                                    <p
                                      className="text-[11px] text-gray-700 -mt-1 text-right font-medium"
                                      data-testid="prep-claim-billing-as-entity"
                                      title="Resolved billing entity — this name lands in Loop 2010AA / CMS-1500 Box 33"
                                    >
                                      → {billingAsName}
                                    </p>
                                  )}
                                  {pbt.length > 0 && (
                                    <p
                                      className="text-[11px] text-gray-400 -mt-1 text-right"
                                      title="Payer-supported billing types from the insurance record"
                                    >
                                      Payer accepts: {pbt.join(', ')}
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                            {noteReviewData.note.assigned_billers && noteReviewData.note.assigned_billers.length > 0 && (
                              <div>
                                <p className="text-gray-600 mb-1">Assigned Billers:</p>
                                {noteReviewData.note.assigned_billers.map(billerId => {
                                  const biller = users.find(u => u.id === billerId);
                                  return biller ? (
                                    <Badge key={billerId} variant="outline" className="mr-1">
                                      {biller.full_name}
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Actions */}
                      <div className="space-y-2">
                        {/* Preview 1500 / 837P (dry run — before creation) — hide for self-pay */}
                        {(() => {
                          const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                          const allIns = getClientAllInsurances(client, insurances);
                          const isSelfPay = allIns.length === 0;
                          return !isSelfPay;
                        })() && (
                        <Button
                          onClick={async () => {
                            setLoadingDryRun(true);
                            try {
                              const resp = await axios.post(`${API}/claims/preview-from-note/${noteReviewData.note.id}`);
                              setDryRunPreviewData(resp.data);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || "Failed to generate preview");
                            } finally {
                              setLoadingDryRun(false);
                            }
                          }}
                          variant="outline"
                          className="w-full border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                          size="lg"
                          disabled={loadingDryRun}
                          data-testid="preview-before-create-btn"
                        >
                          {loadingDryRun ? (
                            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                          ) : (
                            <Eye className="h-5 w-5 mr-2" />
                          )}
                          Preview 1500 / 837P
                        </Button>
                        )}

                        {billingPreviewClaimId ? (
                          <>
                            {(() => {
                              const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                              const allIns = getClientAllInsurances(client, insurances);
                              const isSelfPay = allIns.length === 0;
                              return (
                                <>
                                  <div className={`${isSelfPay ? 'bg-emerald-50 border-emerald-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3 text-center`}>
                                    <p className={`${isSelfPay ? 'text-emerald-800' : 'text-green-800'} font-medium text-sm`}>
                                      {isSelfPay ? 'Client bill processed successfully' : 'Claim created successfully'}
                                    </p>
                                  </div>
                                  {isSelfPay ? (
                                    <Button
                                      onClick={async () => {
                                        // Open invoice PDF in new tab
                                        const clientId = noteReviewData.note.client_id;
                                        try {
                                          const token = sessionStorage.getItem('token');
                                          const resp = await fetch(`${API}/clients/${clientId}/self-pay-invoice`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (resp.ok) {
                                            const blob = await resp.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            window.open(url, '_blank');
                                          }
                                        } catch (e) { console.error('Invoice fetch failed:', e); }
                                        // Close dialog and navigate
                                        setBillingPreviewClaimId(null);
                                        setDryRunPreviewData(null);
                                        setShowNoteReviewDialog(false);
                                        // Optimistic: remove the processed note from billing list
                                        if (noteReviewData?.note?.id) {
                                          setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteReviewData.note.id));
                                          // Also tell the BillingContext (separate state) so the
                                          // Billing tab's "Ready for Billing" list updates without
                                          // a manual refresh.
                                          window.dispatchEvent(new CustomEvent('noteStatusChanged', {
                                            detail: { noteId: noteReviewData.note.id, newStatus: 'billed', noteData: { ...noteReviewData.note, status: 'billed' } }
                                          }));
                                        }
                                        fetchClaimsQueue();
                                        if (clientId) {
                                          const cl = findClientById(clientId);
                                          if (cl) {
                                            setSelectedClient(cl);
                                            setClientDetailTab('billing');
                                            setShowClientDetail(true);
                                            fetchClientBilling(clientId);
                                          }
                                        }
                                      }}
                                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                      size="lg"
                                      data-testid="view-client-bill-btn"
                                    >
                                      <FileText className="h-5 w-5 mr-2" />
                                      View Client Bill / Invoice
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={async () => {
                                        try {
                                          const resp = await axios.get(`${API}/claims/${billingPreviewClaimId}/preview-837p`);
                                          setDryRunPreviewData(resp.data);
                                        } catch (error) {
                                          toast.error("Failed to load claim preview");
                                        }
                                      }}
                                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                                      size="lg"
                                      data-testid="preview-created-claim-btn"
                                    >
                                      <Eye className="h-5 w-5 mr-2" />
                                      View Created Claim & 837P
                                    </Button>
                                  )}
                                </>
                              );
                            })()}
                            <Button
                              onClick={() => {
                                setBillingPreviewClaimId(null);
                                setDryRunPreviewData(null);
                                setShowNoteReviewDialog(false);
                                // Optimistic: remove processed note
                                if (noteReviewData?.note?.id) {
                                  setNotesReadyForBilling(prev => prev.filter(n => n.id !== noteReviewData.note.id));
                                }
                                fetchClaimsQueue();
                              }}
                              variant="outline"
                              className="w-full"
                              size="lg"
                            >
                              Done
                            </Button>
                            {sortedFilteredBillingNotes.length > 0 && (
                              <Button
                                onClick={() => {
                                  // Note already removed from list on claim creation.
                                  // sortedFilteredBillingNotes is already updated.
                                  // currentNoteIndex now points to the next note in
                                  // the biller's currently-sorted view.  Mar 2026:
                                  // was filteredBillingNotes (filter-only) which
                                  // ignored the column-header sort and jumped to a
                                  // random clinician's claim.
                                  const nextIdx = Math.min(currentNoteIndex, sortedFilteredBillingNotes.length - 1);
                                  if (nextIdx >= 0 && sortedFilteredBillingNotes[nextIdx]) {
                                    setCurrentNoteIndex(nextIdx);
                                    setSelectedNoteForReview(sortedFilteredBillingNotes[nextIdx]);
                                    fetchNoteForReview(sortedFilteredBillingNotes[nextIdx].id);
                                    setBillingPreviewClaimId(null);
                                    setDryRunPreviewData(null);
                                  } else {
                                    setShowNoteReviewDialog(false);
                                    setBillingPreviewClaimId(null);
                                    setDryRunPreviewData(null);
                                    toast.info('All notes processed!');
                                  }
                                }}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                size="lg"
                                data-testid="prepare-claim-next-after-create"
                              >
                                Next Claim
                                <ChevronRight className="h-5 w-5 ml-2" />
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {(() => {
                              const client = noteReviewData.client || findClientById(noteReviewData.note.client_id);
                              const allIns = getClientAllInsurances(client, insurances);
                              const isSelfPayNote = allIns.length === 0;
                              const note = noteReviewData.note;
                              const apt = noteReviewData.appointment;
                              const cptCode = note._billing?.cpt_code || note.cpt_code || (apt?.cpt_code_id ? (cptCodes?.find(c => c.id === apt.cpt_code_id)?.code) : null) || (apt?.service_type ? apt.service_type.split(/\s*-\s*/)[0].trim() : null) || null;
                              const hasDiagnosis = (note._billing?.diagnosis_codes || note.diagnosis_codes || []).length > 0 
                                || (noteReviewData.clientActiveDiagnoses || []).length > 0
                                || (note.diagnosis_snapshot?.diagnoses || []).length > 0;
                              const missingFields = [];
                              if (!cptCode) missingFields.push('CPT Code');
                              if (!hasDiagnosis && !isSelfPayNote) missingFields.push('Diagnosis Code');
                              
                              return (
                                <>
                                {missingFields.length > 0 && (
                                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-amber-800 font-medium text-sm">Cannot create claim — missing required fields:</p>
                                    <ul className="text-amber-700 text-xs mt-1 list-disc list-inside">
                                      {missingFields.map(f => <li key={f}>{f}</li>)}
                                    </ul>
                                  </div>
                                )}
                                <Button
                                  onClick={async () => {
                                    const confirmMsg = isSelfPayNote 
                                      ? `Process client bill for this session?` 
                                      : `Create claim for this note?`;
                                    if (!confirm(confirmMsg)) return;
                                    try {
                                      let resp;
                                      try {
                                        resp = await axios.post(`${API}/claims/create-from-note/${noteReviewData.note.id}`);
                                      } catch (err) {
                                        // Jun 2026 — restricted CPT gate: billers/admins may
                                        // explicitly override (logged on the claim).
                                        const det = err?.response?.data?.detail;
                                        if (typeof det === 'string' && det.startsWith('CPT_RESTRICTED:')) {
                                          const msg = det.replace('CPT_RESTRICTED: ', '');
                                          if (!window.confirm(`${msg}\n\nOverride and create the claim anyway? The override is logged with your name.`)) return;
                                          resp = await axios.post(`${API}/claims/create-from-note/${noteReviewData.note.id}?override_cpt_restriction=true`);
                                        } else {
                                          throw err;
                                        }
                                      }
                                      const claimId = resp.data?.claim_id || resp.data?.id;
                                      toast.success(isSelfPayNote ? "Client bill processed" : "Claim created successfully");
                                      const processedNoteId = noteReviewData.note.id;
                                      // Optimistic updates for BOTH state sources so the Billing
                                      // tab's "Ready for Billing" list shrinks instantly without
                                      // a refresh. NotesContext → setter below; BillingContext →
                                      // the noteStatusChanged event.
                                      const newStatus = isSelfPayNote ? 'billed' : 'claim_created';
                                      const notifyBilling = () => window.dispatchEvent(new CustomEvent('noteStatusChanged', {
                                        detail: { noteId: processedNoteId, newStatus, noteData: { ...noteReviewData.note, status: newStatus, is_locked: true, claim_id: claimId } }
                                      }));
                                      if (claimId) {
                                        setBillingPreviewClaimId(claimId);
                                        setNotesReadyForBilling(prev => prev.filter(n => n.id !== processedNoteId));
                                        setNotes(prev => prev.map(n => n.id === processedNoteId ? { ...n, status: newStatus, is_locked: true, claim_id: claimId } : n));
                                        notifyBilling();
                                        fetchClaimsQueue();
                                      } else {
                                        setShowNoteReviewDialog(false);
                                        setNotesReadyForBilling(prev => prev.filter(n => n.id !== processedNoteId));
                                        setNotes(prev => prev.map(n => n.id === processedNoteId ? { ...n, status: newStatus, is_locked: true } : n));
                                        notifyBilling();
                                        fetchClaimsQueue();
                                      }
                                    } catch (error) {
                                      console.error("Failed to create claim:", error);
                                      toast.error(error.response?.data?.detail || (isSelfPayNote ? "Failed to process client bill" : "Failed to create claim"));
                                    }
                                  }}
                                  className={isSelfPayNote 
                                    ? "w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                    : "w-full bg-green-600 hover:bg-green-700 text-white"}
                                  size="lg"
                                  disabled={missingFields.length > 0}
                                >
                                  {isSelfPayNote ? (
                                    <><CreditCard className="h-5 w-5 mr-2" />Process Client Bill</>
                                  ) : (
                                    <><Plus className="h-5 w-5 mr-2" />Create Claim</>
                                  )}
                                </Button>
                                </>
                              );
                            })()}
                            
                            <Button
                              onClick={() => {
                                // The NeedsCorrectionsDialog resets its own
                                // local textarea state when opened.
                                setShowSendBackDialog(true);
                              }}
                              variant="outline"
                              className="w-full border-orange-600 text-orange-600 hover:bg-orange-50"
                              size="lg"
                            >
                              <AlertCircle className="h-5 w-5 mr-2" />
                              Send Back for Corrections
                            </Button>

                            {/* Mark Non-Billable — Feb 2026.
                                Terminal billing-side decision (timely
                                filing expired, no coverage, non-covered
                                service, missing auth, write-off,
                                other).  Distinct from Send Back: this
                                doesn't go to the provider, it just
                                takes the note off the biller queue.
                                Any biller / billing-manager / admin
                                can flip it back if circumstances
                                change. */}
                            <Button
                              onClick={() => {
                                setNotBillableReason('');
                                setNotBillableComment('');
                                setShowNotBillableDialog(true);
                              }}
                              variant="outline"
                              className="w-full border-slate-500 text-slate-700 hover:bg-slate-50"
                              size="lg"
                              data-testid="prepare-claim-mark-not-billable-btn"
                            >
                              <Ban className="h-5 w-5 mr-2" />
                              Mark Non-Billable
                            </Button>

                            {/* Mark this single note as billed in a legacy system
                                (creates a `billed_legacy` stub claim so ERA can match
                                back to it later — no 837P generated). Same UX as the
                                bulk flow on the main Billing tab, just for one note. */}
                            <Button
                              onClick={() => setShowSingleLegacyMarkDialog(true)}
                              variant="outline"
                              className="w-full border-purple-600 text-purple-700 hover:bg-purple-50"
                              size="lg"
                              data-testid="prepare-claim-legacy-bill-btn"
                            >
                              <Archive className="h-5 w-5 mr-2" />
                              Mark as Billed in Legacy System
                            </Button>

                            {/* Refresh from Chart — re-read the latest
                                client chart (insurance, diagnoses) and,
                                if a claim already exists for this note,
                                rebuild it via the same backend endpoint
                                ClaimEditDialog uses.  Pre-submission
                                rebuilds in-place; submitted rebuilds
                                spawn a v2.  When no claim exists yet,
                                we just re-fetch the note review payload
                                so any newly-corrected chart data shows
                                up in the Prepare Claim form. */}
                            {(() => {
                              const existingClaim = (noteReviewData?.existingClaims || []).find(
                                (c) => !c.is_voided && !c.is_superseded
                                  && !['superseded', 'voided'].includes(String(c.status || '').toLowerCase()),
                              );
                              const reviewNoteId = noteReviewData?.note?.id;
                              return (
                                <Button
                                  onClick={async () => {
                                    if (existingClaim?.id) {
                                      const reason = window.prompt(
                                        `Refresh claim ${existingClaim.claim_number || existingClaim.id} from the chart?\n\n`
                                        + 'Re-reads current insurance, diagnoses, CPT, POS, and DOS.\n\n'
                                        + '• Pre-submission claims update in place (no new version)\n'
                                        + '• Already-submitted claims spawn a v2 (old 837 preserved)\n\n'
                                        + 'Reason (recommended):',
                                        'Chart corrected — refreshing claim',
                                      );
                                      if (reason === null) return;
                                      try {
                                        const r = await axios.post(
                                          `${API}/claims/${existingClaim.id}/rebuild-from-chart`,
                                          { reason: reason || 'Refresh from chart' },
                                        );
                                        toast.success(r.data?.message || 'Claim refreshed.');
                                        if (reviewNoteId) await fetchNoteForReview(reviewNoteId);
                                        fetchClaimsQueue();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Refresh failed');
                                      }
                                    } else if (reviewNoteId) {
                                      try {
                                        await fetchNoteForReview(reviewNoteId);
                                        toast.success('Chart data refreshed in form.');
                                      } catch {
                                        toast.error('Failed to refresh from chart');
                                      }
                                    }
                                  }}
                                  variant="outline"
                                  className="w-full border-teal-600 text-teal-700 hover:bg-teal-50"
                                  size="lg"
                                  data-testid="prepare-claim-refresh-from-chart-btn"
                                  title={existingClaim?.id
                                    ? 'Rebuild this claim against the current chart (insurance, dx, CPT). Pre-submission: in-place. Submitted: new version.'
                                    : 'Re-read the latest chart data (insurance, diagnoses) into this form'}
                                >
                                  <RefreshCw className="h-5 w-5 mr-2" />
                                  {existingClaim?.id ? 'Rebuild Claim from Chart' : 'Refresh from Chart'}
                                </Button>
                              );
                            })()}

                            {/* Recover from "orphaned note" data corruption.  When a
                                claim was created but the bidirectional link wasn't
                                written back to the note (legacy bug, fixed forward),
                                the note re-appears on Notes Ready as if no claim
                                exists.  This button looks up matching claims (by
                                note_id, appointment_id, or client+DOS) and lets
                                the biller pick the right one to re-link instead
                                of creating a duplicate. */}
                            <Button
                              onClick={() => setShowLinkExistingClaimDialog(true)}
                              variant="outline"
                              className="w-full border-blue-600 text-blue-700 hover:bg-blue-50"
                              size="lg"
                              data-testid="prepare-claim-link-existing-btn"
                            >
                              <Link2 className="h-5 w-5 mr-2" />
                              Link to Existing Claim
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
  );
};

export default PrepareClaimDialog;
